import { inject } from '@angular/core';
import { FormName } from '@core/data-access/models/form/form.model';
import { FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { selectBuyerPerson } from '@core/features/buyer-information/store/buyer-information.selectors';
import { selectBuyerQuestionnaireDynamicFields } from '@core/features/buyer-questionnaire/store/buyer-questionnaire.selectors';
import { selectTicketHolderQuestionnaireDynamicFields } from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import {
  setSetSyncedQuestionnairesFormFieldsVisibility,
  setSyncedQuestionnaireFormFieldsVisibility
} from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import { enableDisabledFormFields, setDisabledFormFields } from '@core/utils/form-fields/form-field-helpers';
import { removeBuyerInformationFormFields } from '@features/buyer-information/utils/remove-buyer-information-form-fields';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { includes, isEmpty, isNil } from 'lodash';
import { filter, map } from 'rxjs';

export const setBuyerInformationFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      concatLatestFrom(() => store.select(selectBuyerPerson)),
      filter(
        ([{ person, syncForms }, buyerPerson]) =>
          includes(syncForms, FormName.BuyerInformation) && !isNil(person) && person?.uuid === buyerPerson?.uuid
      ),
      map(([{ person, formFields }]) =>
        FormFieldSyncActions.setBuyerInformationFormFields({ person, formFields: enableDisabledFormFields(formFields) })
      )
    ),
  { functional: true }
);

export const setBuyerBillingAddressFormFields$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(({ syncForms }) => includes(syncForms, FormName.BuyerBillingAddress)),
      map(({ formFields }) => FormFieldSyncActions.setBuyerBillingAddressFormFields({ formFields }))
    ),
  { functional: true }
);

export const setBuyerQuestionnaireFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(({ syncForms }) => includes(syncForms, FormName.BuyerQuestionnaire)),
      concatLatestFrom(() => store.select(selectBuyerQuestionnaireDynamicFields)),
      map(([{ person, formFields }, dynamicQuestionnaireFields]) => ({
        person,
        dynamicQuestionnaireFields,
        formFields
      })),
      map(({ dynamicQuestionnaireFields, formFields }) =>
        FormFieldSyncActions.setBuyerQuestionnaireFormFields({
          formFields: setSyncedQuestionnaireFormFieldsVisibility(dynamicQuestionnaireFields, formFields)
        })
      )
    ),
  { functional: true }
);

export const setTicketHolderFormFields$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(({ syncForms }) => includes(syncForms, FormName.TicketHolder)),
      map(({ person, originForm, formFields }) => ({
        person,
        originForm,
        formFields: removeBuyerInformationFormFields(formFields)
      })),
      filter(({ formFields }) => !isEmpty(formFields)),
      map(({ person, originForm, formFields }) => ({
        person,
        formFields: includes([FormName.BuyerInformation, FormName.SavedTicketHolders], originForm)
          ? setDisabledFormFields(person, formFields)
          : formFields
      })),
      map(({ person, formFields }) => FormFieldSyncActions.setTicketHolderFormFields({ person, formFields }))
    ),
  { functional: true }
);

export const setTicketHolderQuestionnaireFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(({ syncForms }) => includes(syncForms, FormName.TicketHolderQuestionnaire)),
      concatLatestFrom(() => store.select(selectTicketHolderQuestionnaireDynamicFields)),
      map(([{ person, formFields }, dynamicQuestionnaireFields]) => ({
        person,
        dynamicQuestionnaireFields,
        formFields
      })),
      map(({ person, dynamicQuestionnaireFields, formFields }) =>
        FormFieldSyncActions.setTicketHolderQuestionnaireFormFields({
          person,
          formFields: setSetSyncedQuestionnairesFormFieldsVisibility(dynamicQuestionnaireFields, formFields)
        })
      )
    ),
  { functional: true }
);

export const setSavedFormSavedTicketHolderFormFields$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(
        ({ originForm, syncForms }) =>
          originForm === FormName.SavedTicketHolders && includes(syncForms, FormName.SavedTicketHolders)
      ),
      map(({ person, formFields }) => FormFieldSyncActions.setSavedTicketHolderFormFields({ person, formFields }))
    ),
  { functional: true }
);

export const setCreateFormSavedTicketHolderFormFields$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setFormFields),
      filter(
        ({ originForm, syncForms }) =>
          originForm === FormName.CreateTicketHolder && includes(syncForms, FormName.CreateTicketHolder)
      ),
      map(({ formFields }) => FormFieldSyncActions.setCreateTicketHolderFormFields({ formFields }))
    ),
  { functional: true }
);
